/**
 * Axios API Client Configuration
 *
 * Centralized API client with:
 * - JWT authentication
 * - Multi-tenant context (X-Tenant-Id header)
 * - Request/response interceptors
 * - Error handling and retry logic
 * - Request cancellation support
 *
 * @see WICL-DOC-002-D API Integration Patterns
 * @see WICL-DOC-001-A Multi-Tenant Architecture
 */

import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";
import i18n from "@/config/i18n";
import { getErrorMessage } from "@/lib/errorMessages";
import { rememberApiErrorToast } from "@/lib/apiErrorToast";

/**
 * API Response wrapper
 */
export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
  errors?: string[];
}

/**
 * API Pagination metadata
 */
export interface ApiPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * API List response with pagination
 */
export interface ApiListResponse<T = unknown> {
  success: boolean;
  error: string | null;
  data: T[];
  pagination: ApiPagination;
}

/**
 * API Error response format
 */
/**
 * Error payload returned by the API.
 *
 * The backend's `ErrorResponse.Create()` always nests the text under `error`:
 * `{ success: false, error: { code, message, details } }`. The flat fields below
 * are kept optional for anything that does not go through BaseApiController
 * (framework ProblemDetails, proxies, legacy endpoints) — read via
 * `extractErrorMessage()` so both shapes are handled.
 */
export interface ApiErrorResponse {
  success?: boolean;
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
  message?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
  timestamp?: string;
  path?: string;
}

/**
 * Axios request config with custom retry settings
 */
export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
  _skipAuthRetry?: boolean; // Skip token refresh retry for logout/auth endpoints
  _skipServerRetry?: boolean; // Let the caller own retry policy (for example React Query)
  _suppressErrorToast?: boolean; // Caller renders an inline, contextual error
  _suppressErrorToastFor?: string[]; // Error codes the caller answers itself (e.g. with a dialog)
}

/**
 * Get environment variable with fallback
 */
const getEnvVar = (key: string, defaultValue: string): string => {
  return import.meta.env[key] || defaultValue;
};

/**
 * Get API Base URL
 * Priority:
 * 1. Runtime config from window.APP_CONFIG (Kubernetes ConfigMap in production)
 * 2. Environment variable VITE_API_BASE_URL (build-time configuration)
 * 3. Empty string for relative URLs (development with Vite proxy)
 */
const getApiBaseUrl = (): string => {
  // Production: Use runtime config from Kubernetes ConfigMap
  if (window.APP_CONFIG?.apiUrl) {
    return window.APP_CONFIG.apiUrl;
  }

  // Build-time: Use environment variable if set
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }

  // Development: Use relative URL (Vite proxy handles routing to localhost:5100)
  return "";
};

/**
 * Base API URL from environment or runtime config
 */
const API_BASE_URL = getApiBaseUrl() + "/api/v1";

/**
 * Request timeout in milliseconds.
 *
 * 90s, and deliberately LONGER than the server, not shorter. Resource endpoints wait on
 * WiService (n8n) webhooks; `cloud-vm/firewall` / `cloud-vm/firewall/macro` were measured in
 * production taking more than 10s, which is what the original value was.
 *
 * The backend's budget is `Math.Max(WiService:Resilience:TimeoutSeconds, WiService:TimeoutSeconds)`
 * — 60s in dev, staging and prod (`values-*.yaml` -> `config.wiService.timeoutSeconds`). At 30s
 * the browser gave up on calls the backend was still legitimately allowed to be making, and the
 * user saw a timeout for a request that then went on to succeed server-side. Whichever side
 * expires first is the side that writes the error message, and the server's is the one with a
 * code in it, so the browser has to be the patient one. 90s leaves a 30s margin over that 60s.
 *
 * Note the env var is build-time only (`import.meta.env`) and no pipeline or chart sets it, so
 * the default below is what actually ships in every environment - this constant IS the timeout.
 */
const API_TIMEOUT = parseInt(getEnvVar("VITE_API_TIMEOUT", "90000"), 10);

/**
 * In-flight token refresh promise, shared across concurrent 401s so that a burst of
 * expired-token requests triggers exactly ONE /auth/refresh call (WICL-404).
 */
let refreshInFlight: Promise<string> | null = null;

/**
 * The lock every tab on this origin competes for before it refreshes.
 *
 * `refreshInFlight` only covers one tab, and the server ROTATES: `RefreshTokenCommandHandler`
 * overwrites the session's refresh-token hash, so the pair a second tab is holding stops working
 * the moment the first tab refreshes. Two tabs that 401 together therefore both send the same
 * token, and the loser is signed out mid-task. Serialising them lets the loser adopt the winner's
 * pair instead.
 */
const REFRESH_LOCK = "wicloud-auth-refresh";

/** Reads the persisted session, in either Zustand persist shape. Null when there is none. */
const readPersistedAuth = (): {
  raw: Record<string, unknown>;
  state: Record<string, unknown>;
  accessToken: string | null;
  refreshToken: string | null;
} | null => {
  const authData = localStorage.getItem("wicloud_auth");
  if (!authData) {
    return null;
  }

  const raw = JSON.parse(authData);
  const state = (raw?.state ?? raw) as Record<string, unknown>;
  return {
    raw,
    state,
    accessToken: (state?.accessToken as string) ?? null,
    refreshToken: (state?.refreshToken as string) ?? null,
  };
};

/**
 * Runs the refresh while holding a lock shared by every tab on this origin.
 *
 * The Web Locks API needs a secure context; where it is missing the call runs unserialised,
 * which is the behaviour this client had before.
 */
const withRefreshLock = async <T>(run: () => Promise<T>): Promise<T> => {
  if (!navigator.locks) {
    return run();
  }

  // Awaited rather than returned straight through: `LockManager.request` is typed to resolve
  // with whatever the callback returns, so handing it back unwrapped reads as Promise<Promise<T>>.
  const result = await navigator.locks.request(REFRESH_LOCK, run);
  return result;
};

/**
 * Thrown by `refreshAuthTokens` when the session is over: there is nothing left to refresh with,
 * or `/auth/refresh` refused the token (401 — `InvalidRefreshTokenException` for an unknown,
 * expired or orphaned session — or 403).
 *
 * Every other failure — a 5xx, a timeout, a dropped connection during a deploy — says nothing
 * about the token and is thrown as it is. Callers end the session only on this one, because ending
 * it removes `wicloud_auth` and so signs the customer out of every open tab.
 */
export class SessionRefusedError extends Error {}

/** The HTTP status of an axios error, read structurally so tests that mock axios still see it. */
const statusOf = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === "number" ? status : undefined;
};

/**
 * Refresh the access token via /auth/refresh.
 *
 * - De-duplicates concurrent calls (see `refreshInFlight`).
 * - Reads/writes the persisted session in the Zustand persist format
 *   (`{ state: { ... } }`).
 * - Correctly unwraps the backend `StandardResponse<T>` envelope
 *   (`response.data.data.accessToken`) — the previous interceptor read
 *   `response.data.accessToken` (undefined), which is why refresh silently failed.
 * - Dispatches `auth:token-refreshed` so the auth store can sync its in-memory
 *   `tokenExpiresAt` in the same tab (the store can't be imported here due to a
 *   circular dependency: api.ts <- authStore.ts <- auth.service.ts <- api.ts).
 *
 * Resolves with the new access token. Rejects with `SessionRefusedError` when the session is
 * over, and with the original error for any failure that is not a refusal.
 */
export const refreshAuthTokens = async (): Promise<string> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  // Captured before the lock so that, once it is held, a changed access token is proof that
  // another tab refreshed while this call was queued.
  const tokenBeforeLock = readPersistedAuth()?.accessToken ?? null;

  refreshInFlight = withRefreshLock(async () => {
    const persisted = readPersistedAuth();
    if (!persisted) {
      throw new SessionRefusedError("No persisted auth session");
    }

    // Another tab already refreshed. Its pair is the live one; refreshing again would rotate
    // the token this tab has just been handed and strand whichever tab asks next.
    if (persisted.accessToken && persisted.accessToken !== tokenBeforeLock) {
      return persisted.accessToken;
    }

    const parsed = persisted.raw;
    const refreshToken = persisted.refreshToken;
    if (!refreshToken) {
      throw new SessionRefusedError("No refresh token available");
    }

    let refreshResponse;
    try {
      refreshResponse = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        // Bounded on purpose: this call is made while holding the cross-tab lock, and axios's
        // own default is no timeout at all, so a hung refresh would stall every other tab too.
        // The admin console's refresh already carries the same ceiling.
        {
          timeout: API_TIMEOUT,
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (error) {
      const status = statusOf(error);
      if (status === 401 || status === 403) {
        throw new SessionRefusedError("Refresh token refused");
      }
      throw error;
    }

    // Backend wraps the payload in StandardResponse<T>: { success, error, data: { ... } }.
    const tokens = refreshResponse.data?.data ?? refreshResponse.data;
    const newAccessToken: string | undefined = tokens?.accessToken;
    const newRefreshToken: string = tokens?.refreshToken ?? refreshToken;
    if (!newAccessToken) {
      throw new Error("Refresh response is missing accessToken");
    }

    // Persist refreshed tokens in the Zustand persist format.
    persisted.state.accessToken = newAccessToken;
    persisted.state.refreshToken = newRefreshToken;
    localStorage.setItem("wicloud_auth", JSON.stringify(parsed));

    // Let the auth store sync same-tab state (tokenExpiresAt) without importing it.
    window.dispatchEvent(
      new CustomEvent("auth:token-refreshed", {
        detail: {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        },
      }),
    );

    return newAccessToken;
  }).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

/**
 * Create Axios instance with default configuration
 */
const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
    },
  });

  /**
   * Request Interceptor
   * - Attach JWT access token
   * - Add X-Tenant-Id header
   * - Log requests in development
   */
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get auth token from localStorage
      const authData = localStorage.getItem("wicloud_auth");
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          // Zustand persist middleware stores data in { state: {...} } structure
          const accessToken = parsed.state?.accessToken || parsed.accessToken;
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        } catch (error) {
          console.error("Failed to parse auth data:", error);
        }
      }

      // Get tenant ID from localStorage
      const tenantData = localStorage.getItem("wicloud_tenant");
      if (tenantData) {
        try {
          const parsed = JSON.parse(tenantData);
          // Zustand persist middleware stores data in { state: {...} } structure
          const currentTenant =
            parsed.state?.currentTenant || parsed.currentTenant;
          if (currentTenant?.id) {
            config.headers["X-Tenant-Id"] = currentTenant.id;
          }
        } catch (error) {
          console.error("Failed to parse tenant data:", error);
        }
      }

      // Log requests in development
      if (import.meta.env.DEV) {
        console.log(
          `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
          {
            params: config.params,
            data: config.data,
            headers: config.headers,
          },
        );
      }

      return config;
    },
    (error: AxiosError) => {
      console.error("[API Request Error]", error);
      return Promise.reject(error);
    },
  );

  /**
   * Response Interceptor
   * - Handle successful responses
   * - Handle error responses (401, 403, 500, etc.)
   * - Parse error messages consistently
   * - Log responses in development
   * - Implement retry logic
   */
  instance.interceptors.response.use(
    (response) => {
      // Log responses in development
      if (import.meta.env.DEV) {
        console.log(
          `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
          {
            status: response.status,
            data: response.data,
          },
        );
      }

      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      const config = error.config as CustomAxiosRequestConfig;
      const showErrorToast = (message: string) => {
        if (config?._suppressErrorToast) return;
        // Suppressed here rather than dismissed by the caller: sonner adds a toast on a timer and
        // dismisses on an animation frame, so a dismiss sent right away can land before the add.
        const code = error.response?.data?.error?.code;
        if (code && config?._suppressErrorToastFor?.includes(code)) return;
        // Remembered so a caller that toasts this same failure replaces the toast instead of
        // stacking a second one on top of it (lib/apiErrorToast.ts).
        rememberApiErrorToast(error, toast.error(message));
      };

      // A cancelled request is not a failure: React Query aborts in-flight queries on
      // unmount, on refetch and when a component's key changes, and the browser aborts
      // every pending request on navigation or reload. Those all land here with no
      // `error.response`, so without this guard they fell through to the network branch
      // below and raised "Unable to connect to server" while the API was perfectly
      // healthy. A real timeout is NOT a cancellation (axios reports ECONNABORTED,
      // `isCancel` is false), so it still reaches the toast.
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      // Log errors in development
      if (import.meta.env.DEV) {
        console.error("[API Response Error]", {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data,
        });
      }

      // Handle different error status codes
      if (error.response) {
        const { status, data } = error.response;

        /**
         * Reads the human-readable message from either error shape, falling back
         * to a translated generic string. Previously this only looked at
         * `data.message`, which the standard `{ error: { message } }` payload
         * never has — so every 4xx surfaced the generic "unexpected server error"
         * instead of what the backend actually said.
         */
        const messageOr = (fallbackKey: string): string =>
          getErrorMessage(error, { fallback: i18n.t(fallbackKey) });

        switch (status) {
          case 401: {
            // Unauthorized - token expired or invalid
            // Skip retry for logout/auth endpoints to prevent infinite loops.
            if (
              config._skipAuthRetry ||
              config.url?.includes("/auth/logout") ||
              config.url?.includes("/auth/login") ||
              config.url?.includes("/auth/register")
            ) {
              return Promise.reject(error);
            }

            // Try to refresh the access token once (WICL-404). The previous guard
            // (`config._retryCount !== undefined`) was ALWAYS false because no caller
            // seeds `_retryCount`, so the refresh branch never executed and every
            // expired-token 401 fell straight through to logging the user out.
            const canRetry = !config._retry && (config._retryCount ?? 0) < 1;

            if (canRetry) {
              config._retry = true;
              config._retryCount = (config._retryCount ?? 0) + 1;

              try {
                const newAccessToken = await refreshAuthTokens();

                // Retry the original request with the new access token.
                if (config.headers) {
                  config.headers.Authorization = `Bearer ${newAccessToken}`;
                }
                return instance(config);
              } catch (refreshError) {
                console.error("Token refresh failed:", refreshError);
                // Not a refusal — a 5xx or a dropped connection says nothing about the token.
                // Fail this request only: clearing the session below would sign the customer
                // out of every open tab over a blip.
                if (!(refreshError instanceof SessionRefusedError)) {
                  return Promise.reject(error);
                }
              }
            }

            // If refresh failed or not attempted, clear persisted auth and signal expiry.
            // Dispatch a custom event instead of hard-navigating so the app can do a
            // soft React Router redirect even when offline (hard navigation requires
            // fetching index.html from the network).
            localStorage.removeItem("wicloud_auth");
            localStorage.removeItem("wicloud_tenant");

            toast.error(i18n.t("errors:auth.tokenExpired"));

            window.dispatchEvent(new CustomEvent("auth:session-expired"));
            break;
          }

          case 400: {
            // Bad Request - the backend rejected the call for a specific,
            // explainable reason (validation, wrong state, business rule). Show
            // that reason rather than letting it fall through to the generic
            // "unexpected server error" default branch.
            const message = messageOr("errors:api.validation");
            showErrorToast(message);
            break;
          }

          case 403: {
            // Forbidden - insufficient permissions
            const message = messageOr("errors:api.forbidden");
            showErrorToast(message);
            break;
          }

          case 404: {
            // Not Found
            const message = messageOr("errors:api.notFound");
            showErrorToast(message);
            break;
          }

          case 422: {
            // Validation Error
            const message = messageOr("errors:api.validation");
            showErrorToast(message);

            // Log validation errors
            if (data?.errors) {
              console.error("Validation errors:", data.errors);
            }
            break;
          }

          case 429: {
            // Too Many Requests
            showErrorToast(i18n.t("errors:api.rateLimited"));
            break;
          }

          case 500:
          case 502:
          case 503:
          case 504: {
            // Server errors - retry with exponential backoff
            const maxRetries = 3;
            const retryCount = config._retryCount || 0;

            if (!config._skipServerRetry && retryCount < maxRetries) {
              config._retryCount = retryCount + 1;

              // Exponential backoff: 1s, 2s, 4s
              const delay = Math.min(1000 * 2 ** retryCount, 30000);

              if (import.meta.env.DEV) {
                console.log(
                  `[API Retry] Attempt ${retryCount + 1}/${maxRetries} after ${delay}ms`,
                );
              }

              await new Promise((resolve) => setTimeout(resolve, delay));
              return instance(config);
            }

            // Max retries reached
            const message = messageOr("errors:api.serverError");
            showErrorToast(message);
            break;
          }

          default: {
            // Generic error
            const message = messageOr("errors:api.serverError");
            showErrorToast(message);
          }
        }
      } else if (error.request) {
        // No response received. A timeout is not a connectivity problem — the request
        // reached the server, which simply took longer than API_TIMEOUT — so telling the
        // user to check their internet connection sends them chasing the wrong thing.
        const isTimeout =
          error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";

        if (!config?.url?.includes("/auth/logout")) {
          showErrorToast(
            i18n.t(
              isTimeout ? "errors:api.timeout" : "errors:api.networkError",
            ),
          );
        }
      } else {
        // Request setup error
        showErrorToast(i18n.t("errors:api.networkError"));
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient();

/**
 * Generic API Client Methods
 * Type-safe CRUD operations
 */
export const api = {
  /**
   * GET request
   */
  get: <T = unknown>(
    url: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<T> => {
    return apiClient
      .get<ApiResponse<T>>(url, config)
      .then((res) => (res.data.data || res.data) as T);
  },

  /**
   * POST request
   */
  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: CustomAxiosRequestConfig,
  ): Promise<T> => {
    return apiClient
      .post<ApiResponse<T>>(url, data, config)
      .then((res) => (res.data.data || res.data) as T);
  },

  /**
   * PUT request
   */
  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: CustomAxiosRequestConfig,
  ): Promise<T> => {
    return apiClient
      .put<ApiResponse<T>>(url, data, config)
      .then((res) => (res.data.data || res.data) as T);
  },

  /**
   * PATCH request
   */
  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: CustomAxiosRequestConfig,
  ): Promise<T> => {
    return apiClient
      .patch<ApiResponse<T>>(url, data, config)
      .then((res) => (res.data.data || res.data) as T);
  },

  /**
   * DELETE request
   */
  delete: <T = unknown>(
    url: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<T> => {
    return apiClient
      .delete<ApiResponse<T>>(url, config)
      .then((res) => (res.data.data || res.data) as T);
  },

  /**
   * GET request for list endpoints with pagination
   */
  getList: <T = unknown>(
    url: string,
    config?: CustomAxiosRequestConfig,
  ): Promise<{
    data: T[];
    pagination: ApiPagination;
  }> => {
    return apiClient.get<ApiListResponse<T>>(url, config).then((res) => ({
      data: res.data.data,
      pagination: res.data.pagination,
    }));
  },
};

/**
 * Request cancellation support
 *
 * @example
 * ```ts
 * const source = createCancelToken();
 *
 * api.get('/users', { cancelToken: source.token })
 *   .catch((error) => {
 *     if (axios.isCancel(error)) {
 *       console.log('Request cancelled');
 *     }
 *   });
 *
 * // Cancel the request
 * source.cancel('Operation cancelled by user');
 * ```
 */
export const createCancelToken = () => axios.CancelToken.source();

/**
 * Check if error is a cancellation
 */
export const isCancel = axios.isCancel;

export default apiClient;
